// O1 · Invite (QO1_Invite): the four portraits, positioning, invite code (POST /api/invite/check) or sign in.
// Admin funnel: o1_view when it shows (with the code prefilled, if any), o1_ok when the code works.
import { useEffect, useState } from 'preact/hooks';
import { CHAR_IDS } from '@shared/types';
import { C } from '@shared/content/characters';
import { POSITIONING } from '@shared/content/lines';
import { Screen, Icon, PrimaryButton, TextButton, TextField, type IconName } from '../../components';
import { api } from '../../api/client';
import { errorText, signIn } from '../../store';
import { go, reset } from '../../router';
import { homeScreen } from '../../home';
import { clearDraft, obStep, setDraft, useDraft } from './draft';
import { mockEnabled } from '../../api/mock';
import { DEMO_INVITE } from '../../api/fixtures';
import s from './O1Invite.module.css';

const ROW_ICONS: IconName[] = ['chat', 'play', 'flame'];

function initialCode(saved: string): string {
  if (saved) return saved;
  try { const q = new URLSearchParams(location.search).get('invite'); if (q) return q.toUpperCase(); } catch { /* ignore */ }
  return mockEnabled() ? DEMO_INVITE : '';
}

export function O1Invite() {
  const saved = useDraft((d) => d.invite);
  const [mode, setMode] = useState<'invite' | 'signin'>('invite');
  const [code, setCode] = useState(() => initialCode(saved));
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { obStep('o1_view', {}, code.trim().toUpperCase()); }, []);

  const cont = async () => {
    const c = code.trim().toUpperCase();
    if (!c) { setErr('Enter your invite code.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await api.inviteCheck(c);
      if (!r.ok) { setErr("That invite code didn't work. Check it and try again."); return; }
      setDraft({ invite: c });
      obStep('o1_ok', {});
      go('O2');
    } catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };

  const doSignIn = async () => {
    if (!email.trim() || !pass) { setErr('Enter your email and password.'); return; }
    setBusy(true); setErr(null);
    try {
      await signIn(email, pass);
      clearDraft();
      reset(homeScreen());
    } catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };

  const switchTo = (m: 'invite' | 'signin') => { setMode(m); setErr(null); };

  return (
    <Screen theme="pre" bg="#100E0C" id="O1">
      <div class={`${s.scroll} xm-scroll`}>
        <div class={s.hero} aria-hidden="true">
          {CHAR_IDS.map((ch) => <img key={ch} src={C[ch].assets.key} alt="" draggable={false} />)}
          <div class={s.heroFade} />
        </div>
        <div class={s.text}>
          <p class={s.label}>{POSITIONING.label}</p>
          <h1 class={s.h1}>{POSITIONING.headline}</h1>
          <p class={s.body}>{POSITIONING.body}</p>
          <div class={s.rows}>
            {POSITIONING.rows.map((t, i) => (
              <div key={t} class={s.row}><Icon name={ROW_ICONS[i]} size={20} color="var(--acc)" /><span>{t}</span></div>
            ))}
          </div>
        </div>
        <div class={s.spacer} />
        {mode === 'invite' ? (
          <div class={s.form} style={{ gap: 10 }}>
            <TextField
              key="invite" label="Invite code" value={code} onInput={(v) => { setCode(v.toUpperCase()); setErr(null); }} variant="mono" h={54}
              placeholder="XM-XXXX" autoCapitalize="characters" autoComplete="off" enterKeyHint="go" onEnter={cont} maxLength={16}
              invalid={!!err} hint={err}
            />
            <PrimaryButton variant="cream" h={56} onClick={cont} busy={busy} data-cta="continue">Continue</PrimaryButton>
            <TextButton weight={600} onClick={() => switchTo('signin')}>I already have an account</TextButton>
          </div>
        ) : (
          <div class={s.form} style={{ gap: 8 }}>
            <TextField key="email" label="Email" type="email" value={email} onInput={(v) => { setEmail(v); setErr(null); }} placeholder="you@email.com" autoComplete="email" inputMode="email" autoCapitalize="none" enterKeyHint="next" />
            <TextField key="password" label="Password" type="password" value={pass} onInput={(v) => { setPass(v); setErr(null); }} autoComplete="current-password" enterKeyHint="go" onEnter={doSignIn} invalid={!!err} hint={err} />
            <PrimaryButton variant="cream" h={54} onClick={doSignIn} busy={busy} style={{ marginTop: 4 }} data-cta="signin">Sign in</PrimaryButton>
            <TextButton weight={600} onClick={() => switchTo('invite')}>I have an invite code</TextButton>
          </div>
        )}
        <p class={s.footer}>{POSITIONING.footer}</p>
      </div>
    </Screen>
  );
}
